import React from "react";
import Box from "@mui/material/Box";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import SbuxStyledTabelSortLabel from "./SbuxStyledTableSortLabel";
import SbuxTranslate from "../SbuxTranslate";
import SbuxStyledTableCell from "./SbuxStyledTableCell";
import Checkbox from "@mui/material/Checkbox";
import styles from "./styles";
import useCss from "../../hooks/useCss";

// Visually hidden styles for accessibility (screen readers only)
const visuallyHidden = {
  border: 0,
  clip: "rect(0 0 0 0)",
  height: "1px",
  margin: "-1px",
  overflow: "hidden",
  padding: 0,
  position: "absolute",
  whiteSpace: "nowrap",
  width: "1px",
} as const;

type Order = "asc" | "desc";

interface HeaderCell {
  id: string;
  label: string;
  align?: "left" | "right" | "center";
  disablePadding?: boolean;
  minWidth?: number;
  sortable?: boolean;
  hide?: boolean;
  checkSelector?: boolean;
  colSpan?: number;
  [key: string]: unknown;
}

interface Props {
  headerGroupCells?: HeaderCell[] | null | undefined;
  headerCells: HeaderCell[];
  order: Order;
  orderBy: string;
  onRequestSort: (event: React.MouseEvent, property: string) => void;
  isTranslationRequired?: boolean;
  handleCheckboxChange?:
    | ((event: React.ChangeEvent<HTMLInputElement>) => void)
    | undefined;
  selectAllCheckbox?: { enabled: boolean; checked: boolean };
}

const SbuxEnhancedTableHead = ({
  headerGroupCells,
  headerCells,
  order,
  orderBy,
  onRequestSort,
  isTranslationRequired,
  handleCheckboxChange,
  selectAllCheckbox = { enabled: false, checked: false },
}: Props) => {
  const classes = useCss(styles);
  const createSortHandler = (property: string) => (event: React.MouseEvent) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      {headerGroupCells && (
        <TableRow>
          {headerGroupCells.map((headerGroupCell, index) => (
            <React.Fragment key={`th-${index}`}>
              {!headerGroupCell?.hide && (
                <SbuxStyledTableCell
                  align={"center"}
                  padding={headerGroupCell.disablePadding ? "none" : "normal"}
                  colSpan={headerGroupCell.colSpan}
                >
                  {isTranslationRequired ? (
                    <SbuxTranslate>{headerGroupCell.label}</SbuxTranslate>
                  ) : (
                    <>{headerGroupCell.label}</>
                  )}
                </SbuxStyledTableCell>
              )}
            </React.Fragment>
          ))}
        </TableRow>
      )}
      <TableRow>
        {headerCells &&
          headerCells.map((headerCell, index) => (
            <React.Fragment key={index}>
              {!headerCell?.hide && (
                <SbuxStyledTableCell
                  align={headerCell.align}
                  padding={headerCell.disablePadding ? "none" : "normal"}
                  width={headerCell.minWidth}
                  sortDirection={orderBy === headerCell.id ? order : false}
                  className={
                    headerCell.checkSelector
                      ? classes.selectAllColumn
                      : undefined
                  }
                >
                  {headerCell.sortable ? (
                    <SbuxStyledTabelSortLabel
                      active={orderBy === headerCell.id}
                      direction={orderBy === headerCell.id ? order : "asc"}
                      onClick={createSortHandler(headerCell.id)}
                    >
                      {isTranslationRequired ? (
                        <SbuxTranslate>{headerCell.label}</SbuxTranslate>
                      ) : (
                        <>{headerCell.label}</>
                      )}
                      {orderBy === headerCell.id ? (
                        <Box component="span" sx={visuallyHidden}>
                          {order === "desc"
                            ? "sorted descending"
                            : "sorted ascending"}
                        </Box>
                      ) : null}
                    </SbuxStyledTabelSortLabel>
                  ) : isTranslationRequired ? (
                    headerCell.checkSelector && selectAllCheckbox.enabled ? (
                      <Checkbox
                        inputProps={{ "aria-label": "controlled" }}
                        disableFocusRipple
                        disableRipple
                        checked={selectAllCheckbox.checked}
                        onChange={handleCheckboxChange}
                        className={classes.cellCheckbox}
                      />
                    ) : (
                      <SbuxTranslate>{headerCell.label}</SbuxTranslate>
                    )
                  ) : (
                    <>{headerCell.label}</>
                  )}
                </SbuxStyledTableCell>
              )}
            </React.Fragment>
          ))}
      </TableRow>
    </TableHead>
  );
};

export default SbuxEnhancedTableHead;
